/**
 * Compares two objects or values deeply and returns only the changes.
 *
 * @param {any} orig - The original object/value.
 * @param {any} updated - The updated object/value.
 * @param {Object} [options] - Configuration options.
 * @param {string} [options.idKey='id'] - Property name used to match array items across objects.
 * @param {boolean} [options.includeMetadata=true] - If false, excludes `_action` tracking fields.
 * @returns {any} The delta/diff object containing only changed attributes.
 */
function parseObject(orig, updated, options = {}) {
    const { idKey = "id", includeMetadata = false } = options;

    // 1. Identical references or primitive equality
    if (orig === updated) return undefined;

    // 2. Handle cases where either value is null, undefined, or primitive types
    if (
        orig === null ||
        updated === null ||
        typeof orig !== "object" ||
        typeof updated !== "object"
    ) {
        return updated;
    }

    // 3. Handle Dates
    if (orig instanceof Date || updated instanceof Date) {
        if (
            orig instanceof Date &&
            updated instanceof Date &&
            orig.getTime() === updated.getTime()
        ) {
            return undefined;
        }
        return updated;
    }

    // 4. Handle Arrays
    if (Array.isArray(orig) || Array.isArray(updated)) {
        if (!Array.isArray(orig) || !Array.isArray(updated)) {
            return updated;
        }

        const arrayDiffs = [];

        // Map original items by their ID for fast lookup
        const origMap = new Map();
        orig.forEach((item, index) => {
            if (item && typeof item === "object" && item[idKey] !== undefined) {
                origMap.set(item[idKey], { item, index });
            }
        });

        const matchedOrigIds = new Set();

        // Iterate through updated items
        for (let i = 0; i < updated.length; i++) {
            const updatedItem = updated[i];
            const hasId =
                updatedItem &&
                typeof updatedItem === "object" &&
                updatedItem[idKey] !== undefined;

            // Case A: Item has no ID -> Always treat as a new addition
            if (!hasId) {
                arrayDiffs.push(
                    includeMetadata
                        ? { _action: "added", value: updatedItem }
                        : updatedItem
                );
                continue;
            }

            const id = updatedItem[idKey];
            const origMatch = origMap.get(id);

            if (origMatch) {
                // Case B: Existing item found by ID -> Deep diff the two objects
                matchedOrigIds.add(id);
                const itemDiff = parseObject(
                    origMatch.item,
                    updatedItem,
                    options
                );

                if (itemDiff !== undefined) {
                    // Keep the idKey in the output so you know which record changed
                    const diffWithId = { [idKey]: id, ...itemDiff };

                    arrayDiffs.push(
                        includeMetadata
                            ? { _action: "modified", diff: diffWithId }
                            : diffWithId
                    );
                }
            } else {
                // Case C: Item has an ID, but it's not in orig -> Treat as new record
                arrayDiffs.push(
                    includeMetadata
                        ? { _action: "added", value: updatedItem }
                        : updatedItem
                );
            }
        }

        // Case D: Find removed items (were in orig, but missing from updated)
        for (const [id, { item }] of origMap.entries()) {
            if (!matchedOrigIds.has(id) && includeMetadata) {
                arrayDiffs.push({ _action: "removed", value: item });
            }
        }

        return arrayDiffs.length > 0 ? arrayDiffs : undefined;
    }

    // 5. Handle Objects
    const diff = {};
    const allKeys = new Set([...Object.keys(orig), ...Object.keys(updated)]);

    for (const key of allKeys) {
        if (!(key in updated)) {
            if (includeMetadata) {
                diff[key] = { _action: "removed", oldValue: orig[key] };
            }
        } else if (!(key in orig)) {
            diff[key] = updated[key];
        } else {
            const childDiff = parseObject(orig[key], updated[key], options);
            if (childDiff !== undefined) {
                diff[key] = childDiff;
            }
        }
    }

    return Object.keys(diff).length > 0 ? diff : undefined;
}

export { parseObject };
